import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Card as CardMUI, 
  Button, 
  Grid,
  Typography } from '@material-ui/core';

import { 
  LocationOn,
} from '@mui/icons-material';

import NotificationIcon from 'assets/img/icons/vehicle-tracking.svg';
import { useStyles, customCardStyles } from './Telemetry.styled';
import { TelemetryActions } from 'state/modules/telemetry';
import { EditTelemetryModal } from 'components/Modals';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import TelemetryStats from './TelemetryStats';
import TelemetryMap from './TelemetryMap';
import Loading from 'components/Loading';
import { Card } from 'components/UI';
import { useConfirm } from 'hooks';
import { capitalize } from 'utils';
import moment from 'moment';
import i18n from 'i18n-js';
import Can from 'components/Can';

const centerFallback = { lat: 34.05, lng: -118.3 };

export const DatetimeField = ({ date, localeFormat, timezone = moment.tz.guess() }) => {
  const classes = useStyles();
  const time = timezone ? moment(date).tz(timezone) : moment(date);
  const timezoneFormat = (
    <span className={classes.timezone}>
      ({timezone ? moment().tz(timezone).format('UTC Z') : moment().format('UTC Z')})
    </span>
  );

  return (
    <>
      {' '}
      {time.format(`${localeFormat} hh:mm A`)} {timezoneFormat}{' '}
    </>
  );
};

const Telemetry = ({ vehicleId, timezone }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cardClasses = customCardStyles();
  const [open, setOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const [toggleBlockedData, ShowBlockedDataConfirm] = useConfirm({
    title: capitalize(i18n.t('telemetry.load_data')),
    message: i18n.t('telemetry.agree_text'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('telemetry.continue'),
    onConfirm: () => {
      setAcceptTerms(true);
      updateGpsLocation(true);
    },
  });

  const { data, loading } = useSelector((state) => state.telemetry);

  const loadTelemetry = useCallback(
    (params = {}) => {
      dispatch(TelemetryActions.fetchTelemetry(vehicleId, params));
    },
    [dispatch, vehicleId]
  );

  const updateGpsLocation = (terms) => {
    let params = {};
    if (terms || acceptTerms) {
      params.agree_terms = true;
    }
    loadTelemetry(params);
  };

  const handleModalOpen = () => {
    setOpen((prevState) => !prevState);
  };

  useEffect(() => {
    loadTelemetry();
  }, [loadTelemetry]);

  const items = [
    {
      title: i18n.t('telemetry.edit'),
      onClick: handleModalOpen,
    },
    {
      title: i18n.t('telemetry.update_gps'),
      onClick: updateGpsLocation,
    },
  ];

  const distanceToLocationValue = data?.distance_to_location?.magnitude?.toString() || '';
  const distanceToLocationUnit = data?.distance_to_location?.units || '';
  const distanceToLocation = distanceToLocationValue
  ? `${distanceToLocationValue} ${distanceToLocationUnit}`
  : null;
  const showLocationCard = data?.distance_to_location || data.gps_coordinates || data?.gps_blocked ? true : false;

  const renderLocationCard = () => {
    return (
      <CardMUI>
        <Box position="relative">
          <Box className={classes.boxContainer} style={{ filter: data.gps_blocked ? 'blur(5px)' : 'none' }}>
            <Box
              mb={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid container>
                <Grid item xs={4} data-cy="location-title">
                  <Typography variant="subtitle2" fontWeight="bold">
                    {i18n.t('common.location')}{' '}
                  </Typography>
                </Grid> 
                { data?.gps_time && (
                  <Grid item xs={8}>
                    <Box width="100%" display="flex" justifyContent="flex-end">
                      <Typography variant="caption">
                        <Box>
                          {i18n.t('telemetry.time_label')}{' '}
                          <DatetimeField
                            timezone={timezone}
                            date={data.gps_time}
                            localeFormat={localeFormat}
                          />
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
            
            { distanceToLocation && (
              <Grid container>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationOn 
                      style={{
                      color: "#387FF7",
                      marginRight: 10,
                    }}/>
                    <Typography variant="subtitle2">
                      {i18n.t('telemetry.distance_location')}:
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box>
                    <Typography variant="caption">
                      <Box fontSize={13} display="flex" justifyContent="flex-end">
                        <span style={{ fontWeight: 400 }}>
                          {' '}
                          { distanceToLocation }
                        </span>
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Grid container style={{ marginBottom: 30 }}>
              <Grid item xs={10}>
                <Box display="flex" alignItems="center" mb={2}>
                  <GpsFixedIcon className={classes.gpsIcon} />
                  <Typography variant="subtitle2">
                    {i18n.t('telemetry.gps')}: {i18n.t('telemetry.gps_coordinates')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Box>
                  {data?.gps_coordinates && (
                    <Box fontSize={13} display="flex" justifyContent="flex-end">
                      <Typography variant="caption">
                        
                          <span style={{ fontWeight: 400 }}>
                            {data.gps_coordinates.lat}, {data.gps_coordinates.lng}
                          </span>
                        
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            { renderMap(data?.gps_coordinates) }
            
          </Box>

          { data.gps_blocked && (
            <Box
              className={classes.notificationContainer}
              justifyContent="center"
              alignItems="center"
              position="absolute"
              display="flex"
              top={0}
              left={0}
              right={0}
              bottom={0}>
              <Box className={classes.notificationContent}>
                <img
                  className={classes.notificationIcon}
                  src={NotificationIcon}
                  alt="notification-icon"
                />
                <p className={classes.notificationTitle}>
                  {i18n.t('telemetry.restricted_access')}
                </p>
                <p className={classes.notificationSubtitle}>{i18n.t('telemetry.data_hidden')}</p>
                <Can I="read_inprogress" a="telemetry">
                  <Button onClick={toggleBlockedData} variant="outlined" color="primary">
                    {i18n.t('telemetry.load_data')}
                  </Button>
                </Can>
              </Box>
            </Box>
          )}
        </Box>
      </CardMUI>
    )
  }


  const renderMap = (coordinates) => {

    if (!data.gps_coordinates && !data.gps_blocked) {
      return (
        <Typography variant="h3" color="textPrimary">
          <Box fontSize={13} align="center">
            {i18n.t('telemetry.no_gps_location')}
          </Box>
        </Typography>
      );
    }

    const hasShowableCoordinates = data.gps_coordinates && !data.gps_blocked;

    return (
      <TelemetryMap
        offsetLat={0}
        locations={[
          {
            lat: hasShowableCoordinates ? coordinates.lat : centerFallback.lat,
            lng: hasShowableCoordinates ? coordinates.lng : centerFallback.lng,
            icon: 'pickup',
          },
        ]}
      />
    );
  };

  return (
    <Card
      menuItems={items}
      data-cy="telemetry"
      headerActionType="vertical"
      contentRootClasses={cardClasses}
      title={i18n.t('telemetry.title')}
    >
      <Box>

        {loading ? (
          <Box position="relative">
            <Box
              className={classes.loadingContainer}
              justifyContent="center"
              alignItems="center"
              position="absolute"
              display="flex"
              top={0}
              left={0}
              right={0}
              bottom={0}
            >
              <Loading />
            </Box>
          </Box>
        ) : (
          <>
            <TelemetryStats
              telemetry={data}
              timezone={timezone}
              onUpdate={handleModalOpen}
              localeFormat={localeFormat} />
            { showLocationCard && renderLocationCard() }
          </>
        )}
        
        {open && <EditTelemetryModal open={open} telemetry={data} handleClick={handleModalOpen} />}
        <ShowBlockedDataConfirm />
      </Box>
    </Card>
  );
};

export default Telemetry;
